//react
import React, {
  Fragment,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  BrowserRouter as Router,
  Route,
  useHistory,
  useLocation,
} from "react-router-dom";
import "./App.css";
import Routes from "./route/Routes";
import UserRoute from "./route/UserRoute";

//Azure
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { loginRequest } from "./azure/authConfig";
import LoginRedirect from "./azure/LoginRedirect";
import { switchAccount } from "./azure/switchAccount";

//context
import { LoadingContextProvider } from "./system/context/LoadingContext";
import {
  UserContextProvider,
  useUserDispatch,
} from "./system/context/UserContext";

//object
import { MsUser } from "./system/types/interface";
import { CommonRoutes } from "./system/types/type";

//lib
import {
  InteractionRequiredAuthError,
  BrowserAuthError,
} from "@azure/msal-browser";

// recoil
import { RecoilRoot } from "recoil";
import PWAInstallPrompt from "./components/Main/PWAInstallPrompt";
import NonInbodyNotice from "./components/NonInbodyNotice";
import GlobalStyle from "./styles/GlobalStyle";
import "./system/syncfusionLocale";

// utils
import { ServiceWorkerManager } from "./hooks/PWA/serviceWorkerManager";
import { isIOS, isPWA } from "./hooks/PWA/pwaUtils";

//var
let dupCheck = true;

function App() {
  useEffect(() => {
    // iOS PWA인 경우 Service Worker 초기화를 건너뜀
    if (isIOS() && isPWA()) {
      console.log("iOS PWA detected - skipping Service Worker initialization");
      return;
    }

    // iOS가 아닌 경우에만 Service Worker 초기화
    if (!isIOS()) {
      const swManager = ServiceWorkerManager.getInstance();
      swManager.initialize();

      return () => {
        swManager.cleanup();
      };
    }
  }, []);
  return (
    <RecoilRoot>
      <GlobalStyle />
      <UnauthenticatedTemplate>
        <LoginRedirect></LoginRedirect>
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <UserContextProvider>
          <LoadingContextProvider>
            <RecoilRoot>
              {/* iOS가 아닌 경우에만 PWA 설치 프롬프트 표시 */}
              {!isIOS() && <PWAInstallPrompt />}
              <UserWrapper></UserWrapper>
            </RecoilRoot>
          </LoadingContextProvider>
        </UserContextProvider>
      </AuthenticatedTemplate>
    </RecoilRoot>
  );
}

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pathname]);

  return null;
};

function UserWrapper(): ReactElement {
  const dispatch = useUserDispatch();
  const { instance, accounts } = useMsal();
  const [isInit, setIsInit] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const userRef = useRef<MsUser | undefined>();
  const history = useHistory();

  React.useEffect(() => {
    // sso-login
    const ssoLogin = async () => {
      // 타 도메인(비 @inbody.com) 계정으로 접속한 경우:
      // 흰 화면으로 멈추지 않고 안내 카드를 노출한 뒤 차단한다.
      const hasInbodyAccount = accounts.some((x) =>
        x.username.includes("@inbody.com")
      );
      if (accounts.length > 0 && !hasInbodyAccount) {
        setBlocked(true);
        return;
      }

      const idToken = await msalLogin();
      if (idToken === null || idToken === undefined) {
        return;
      }

      try {
        //token decoding
        var base64Payload = idToken.split(".")[1]; //value 0 -> header, 1 -> payload, 2 -> VERIFY SIGNATURE
        var payload = Buffer.from(base64Payload, "base64");
        var result = JSON.parse(payload.toString());
        userRef.current = result;

        // 유저 정보
        if (userRef.current !== undefined) {
          dispatch({ type: "LOGIN", item: userRef.current });
        }

        //메인페이지로 이동
        history.push("/");
      } catch (error: any) {
        if (error.response) {
          if (error.response.status === 401) {
            msalLogin();
          } else {
            alert("등록되지 않은 이메일로 접속이 불가능합니다.");
            window.location.replace("https://weareinbody.com");
          }
        }
      } finally {
        setIsInit(true);
      }
    };

    const msalLogin = async () => {
      let response;
      try {
        // lookinbody.com 메일 있는 경우 inbody.com 계정 찾는 부분
        const account = accounts.find((x) =>
          x.username.includes("@inbody.com")
        );

        if (account?.username) {
          try {
            response = await instance.ssoSilent({
              scopes: loginRequest.scopes,
              sid: account?.idTokenClaims?.sid,
            });
          } catch (err) {
            console.log("SSO silent login fail!");
            try {
              response = await instance.acquireTokenSilent({
                scopes: loginRequest.scopes,
                account: account,
              });
            } catch (error) {
              console.log("Token silent login fail!");
              if (err instanceof InteractionRequiredAuthError) {
                try {
                  response = await instance.acquireTokenPopup({
                    ...loginRequest,
                  });
                } catch (err) {
                  console.log(err);
                  if (err instanceof BrowserAuthError) {
                    if (err.message.includes("user_cancelled")) {
                      alert(
                        "로그인을 취소하셨습니다.\n정상적인 사이트 이용이 불가능합니다."
                      );
                      window.location.replace("https://weareinbody.com");
                    } else if (err.message.includes("popup_window_error"))
                      alert(
                        "팝업 차단 기능이 설정되어 있습니다.\n\n팝업 차단을 '해제'하고 '새로고침' 버튼을 눌러주세요."
                      );
                  } else {
                    alert("로그인에 실패했습니다. 관리자에게 문의해주세요.");
                  }
                }
              }
            }
          }
        }
        return response?.idToken;
      } catch (error) {
        console.error(error);
        return null;
      }
    };
    if (dupCheck) {
      ssoLogin();
      dupCheck = !dupCheck;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, dispatch, instance]);

  if (blocked) {
    return <NonInbodyNotice onSwitchAccount={switchAccount} />;
  }

  if (isInit) {
    return (
      <Router>
        <ScrollToTop />
        <Route path={CommonRoutes.login}></Route>
        <UserRoute Component={Routes}></UserRoute>
      </Router>
    );
  } else {
    return <Fragment></Fragment>;
  }
}

export default App;
